import { BrandVariablesFragment } from '@/lib/graphql/shared';
import { IdentityProvider } from '@/lib/graphql/types';
import { createContext } from 'react';

export interface TenantFeatures {
  enableNonIntegratedScheduling?: boolean;
  enableHomeView?: boolean;
  enableJourneyView?: boolean;
  enableAgentCtaAfterTask?: boolean;
  disableConventionalAuthentication?: boolean;
}

export interface EnrollmentConfig {
  readonly steps: [string, ...string[]];
}

export interface CognitoConfig {
  clientId: string;
  userPoolId: string;
  identityProviders: IdentityProvider[];
  firstPartyLogin: boolean;
  requireEligibilityToken: boolean;
}

export type HomepageWidget =
  | 'findCareCard'
  | 'otherWorkBenefits'
  | 'preventativeToDoList'
  | 'welcomeWidget';

export interface HomepageWidgetConfig {
  readonly component: HomepageWidget;
  readonly props?: Record<string, unknown>;
}

/**
 * We parse the tenant data object in order to provide a cleaner configuration object where possible.
 */
export interface TenantConfiguration {
  id: string;
  domain: string;
  name: string;
  slug: string;
  features: TenantFeatures;
  branding: BrandVariablesFragment | null;
  cognito: CognitoConfig;
  mapboxConfig: {
    accessToken?: string;
  };
  contentfulConfig: {
    spaceId: string;
    environment: string;
    accessToken: string;
  };
  enrollmentConfig: EnrollmentConfig | null;
  homepageConfig: HomepageWidgetConfig[];
}

export interface TenantConfigurationProps {
  config: TenantConfiguration | null;
  setConfig: (config: TenantConfiguration) => void;
}

// We use `never` and a cast, to ensure that `TenantConfigContext.Provider` can not be used without a value.
export const TenantConfigContext = createContext<
  TenantConfigurationProps | never
>(null as never);
